import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Paper,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { getAccount } from "../../api/account";
import { getManagerLevel } from "../../api/account/getManagerLevel";
import AccountInformation from "../../components/Accounts/AccountInformation";
import { Files } from "../../components/Files";
import { ManagersList } from "../../components/Managers";
import { ProjectsList } from "../../components/Projects";
import { useTranslationHook } from "../../hooks";
import { setLoading } from "../../store/UI";
import { Account, ManagerLevel } from "../../types/model/account";
import { StoreInterfaces } from "../../types/store";
import AccountUsers from "../Admin/Users/AccountUsers";

const DetailsAccount = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslationHook();

  const { accountId } = useParams<{ accountId: string }>();

  const session = useSelector((state: StoreInterfaces) => state.session);

  const [accountData, setAccountData] = useState<Account>();
  const [accountLoaded, setAccountLoaded] = useState(false);
  const [managerLevel, setManagerLevel] = useState<ManagerLevel>();
  const [administratorAccount, setAdministratorAccount] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const isTenderUser =
    !!session?.accessToken &&
    !session.isAdministrator &&
    !session.isManager;

  const readOnly =
    session.isAdministrator
      ? false
      : isTenderUser ||
        (!!managerLevel && managerLevel.level !== "account");
  const fetchManagerLevel = async () => {
    try {
      const data = await getManagerLevel(Number(accountId));
      setManagerLevel(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAccount = async () => {
    dispatch(setLoading(true));
    try {
      const data = await getAccount(Number(accountId));
      setAccountData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setAccountLoaded(true);
      dispatch(setLoading(false));
    }
  };

  const fetchAdministratorStatus = async () => {
    try {
      const data = await getAccount(Number(accountId));
      setAdministratorAccount(data.isAdministrator);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchManagerLevel();
    fetchAccount();
    fetchAdministratorStatus();
  }, [accountId]);

  if (!accountLoaded || !accountData) {
    return <CircularProgress />;
  }

  const allTabs = [
    {
      id: "details",
      label: t("admin.accounts.account-tab-details"),
      hiddenForAdmin: false,
      component: accountData && managerLevel ? (
        <AccountInformation
          accountData={accountData}
          onRefresh={fetchAccount}
          readOnly={readOnly}
        />
      ) : null,
    },
    {
      id: "users",
      label: t("admin.accounts.account-tab-users"),
      hiddenForAdmin: false,
      component: managerLevel && (
        <AccountUsers
          accountId={Number(accountId)}
          administrationAccount={administratorAccount}
          readOnly={readOnly}
        />
      ),
    },
    {
      id: "managers",
      label: t("admin.accounts.account-tab-managers"),
      hiddenForAdmin: false,
      component: managerLevel && (
        <ManagersList
          accountId={Number(accountId)}
          managerLevel={managerLevel}
          isObjectClosedOrCanceled={false}
          readOnly={readOnly}
        />
      ),
    },
    {
      id: "projects",
      label: t("admin.accounts.account-tab-projects"),
      hiddenForAdmin: true,
      component: managerLevel && (
        <ProjectsList accountId={Number(accountId)} readOnly={readOnly} />
      ),
    },
    {
      id: "files",
      label: t("generic.files"),
      hiddenForAdmin: true,
      component: managerLevel && (
        <Files accountId={Number(accountId)} readOnly={readOnly} />
      ),
    },
  ];

  const tabs = allTabs.filter(
    (tab) => !tab.hiddenForAdmin || !session.isAdministrator,
  );

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100%" }} variant="h4">
          {t("generic.account")} - {accountData.name}
        </Typography>
        {!isTenderUser && (
          <Tooltip title={t("generic.back")}>
            <Button
              onClick={() => navigate("/accounts/list")}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              <ArrowBackIcon />
            </Button>
          </Tooltip>
        )}
      </Toolbar>

      <Tabs
        value={tabIndex}
        onChange={(_e, newValue) => setTabIndex(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        {tabs.map((tab, i) => (
          <Tab key={tab.id} label={tab.label} />
        ))}
      </Tabs>

      <Box sx={{ p: 3 }}>
        {tabs.map((tab, i) => (
          <Fade
            key={tab.id}
            in={tabIndex === i}
            timeout={{ enter: 200, exit: 0 }}
            unmountOnExit
          >
            <Box>{tab.component}</Box>
          </Fade>
        ))}
      </Box>
    </Paper>
  );
};

export default DetailsAccount;
